/**
 * Feature E02-F04-T04 — "CRUD project_tasks (статус, переупорядочивание)"
 * request/response contracts.
 *
 * The project-side kick-off checklist (`project_tasks`): create / update status /
 * reorder (the `position` column) / delete, all scoped to a `project_id`. The
 * list is always served ORDER BY position ASC (the order `get_1_project` returns
 * tasks in — see `project-detail-card.ts → detailTaskSchema`).
 *
 * Legacy parity (schema.sql `project_tasks` + branch-2 tasks LATERAL): a task has
 * `type` (free-form bucket, e.g. 'kickoff'), `text` (the checklist line),
 * `status` (default 'open', toggled to 'done'), `position` (INTEGER ordering) and
 * a `meta` jsonb for arbitrary extra data. Deletion is a plain row delete (the FK
 * to projects is ON DELETE CASCADE, so deleting the parent project removes its
 * tasks — but deleting one task touches nothing else).
 *
 * IDOR invariant: the project ownership is enforced by the route from the SESSION
 * principal (same access scope as `get_1_project`); the body never carries a
 * user/owner id and `project_id` comes from the URL, never the body.
 *
 * Lives in its OWN file (not re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Task status domain (legacy parity: default 'open', toggled to 'done')
// ---------------------------------------------------------------------------
/** A project_tasks.status value. 'open' (default) | 'done'. */
export const PROJECT_TASK_STATUSES = ['open', 'done'];
/** project_tasks.status — open | done; nullable on read (legacy rows). */
export const projectTaskStatusSchema = z.enum(PROJECT_TASK_STATUSES);
// ---------------------------------------------------------------------------
// Read DTO — one project_tasks row (ORDER BY position ASC)
// ---------------------------------------------------------------------------
/**
 * One project task row. Mirrors `project_tasks` (schema.sql) and the
 * `detailTaskSchema` projection used by the detail card, so the list endpoint and
 * the get_1_project card never drift.
 */
export const projectTaskItemSchema = z.object({
    id: z.number().int(),
    project_id: z.number().int(),
    /** Free-form bucket, e.g. 'kickoff'. Nullable on legacy rows. */
    type: z.string().nullable(),
    /** The checklist line. */
    text: z.string(),
    /** open | done — nullable on read for legacy rows. */
    status: z.string().nullable(),
    /** INTEGER ordering — the list is ORDER BY position ASC. */
    position: z.number().int(),
    /** Arbitrary extra data (jsonb). */
    meta: z.unknown(),
    /** ISO 8601. */
    created_at: z.string(),
});
/** GET /projects/:id/tasks → bare array, ORDER BY position ASC. */
export const projectTaskListSchema = z.array(projectTaskItemSchema);
// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------
/**
 * POST /projects/:id/tasks — create a task. `position` is optional: when omitted
 * the new task is appended (MAX(position)+1). `status` defaults to 'open',
 * `type` defaults to 'kickoff', `meta` defaults to {}.
 */
export const createProjectTaskInputSchema = z.object({
    text: z.string().min(1),
    type: z.string().optional(),
    status: projectTaskStatusSchema.optional(),
    position: z.number().int().optional(),
    meta: z.record(z.unknown()).optional(),
});
// ---------------------------------------------------------------------------
// Update (status / text / meta)
// ---------------------------------------------------------------------------
/**
 * PATCH /projects/:id/tasks/:taskId — update a task's status / text / meta.
 * Every field optional (a status-only toggle is the common case). At least one
 * field is required (validated by the route/service so an empty body is a 400).
 */
export const updateProjectTaskInputSchema = z
    .object({
    text: z.string().min(1).optional(),
    type: z.string().optional(),
    status: projectTaskStatusSchema.optional(),
    meta: z.record(z.unknown()).optional(),
})
    .refine((v) => v.text !== undefined ||
    v.type !== undefined ||
    v.status !== undefined ||
    v.meta !== undefined, { message: 'At least one field must be provided' });
// ---------------------------------------------------------------------------
// Reorder (positions)
// ---------------------------------------------------------------------------
/**
 * PUT /projects/:id/tasks/reorder — set the absolute order of the project's
 * tasks. `order` is the full list of task ids in their new order; the service
 * writes position=0..N-1 accordingly. Ids not belonging to the project are
 * ignored (the service re-reads the project's own tasks).
 */
export const reorderProjectTasksInputSchema = z.object({
    order: z.array(z.number().int()).min(1),
});
// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------
/** A single-task mutation result (create/update) → the resulting row. */
export const projectTaskResultSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    task: projectTaskItemSchema,
});
/** Delete / reorder ack → the resulting full task list (ORDER BY position ASC). */
export const projectTasksAckSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    tasks: projectTaskListSchema,
});
//# sourceMappingURL=project-tasks.js.map